import React, { useState, useEffect } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableRow,
    TableHead, Paper, Button, TextField, TablePagination,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Grid, Box, Typography, ButtonGroup, Autocomplete, Chip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import { useUser } from '../contexts/UserContext';
import { useAccesos } from '../contexts/AccesosContext';
import Swal from 'sweetalert2';

function ConfigProyecciones() {
    const { tienePermiso } = useAccesos();
    const { user } = useUser();
    const server = process.env.REACT_APP_API_ROUTE;

    const [proyecciones, setProyecciones] = useState([]);
    const [editingItem, setEditingItem] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [openDialog, setOpenDialog] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [formData, setFormData] = useState({ nombre: '', codigo: '' });
    const [loading, setLoading] = useState(false);

    // Asignación a productos
    const [openAsignar, setOpenAsignar] = useState(false);
    const [asignarItem, setAsignarItem] = useState(null);
    const [productos, setProductos] = useState([]);
    const [productosSeleccionados, setProductosSeleccionados] = useState([]);

    const fetchProyecciones = async () => {
        try {
            const response = await fetch(`${server}/ImgProyecciones`);
            const data = await response.json();
            setProyecciones(data.$values || data || []);
        } catch (error) {
            console.error('Error:', error);
            Swal.fire('Error', 'Error al cargar proyecciones', 'error');
        }
    };

    const fetchProductos = async () => {
        try {
            const response = await fetch(`${server}/ImgProyecciones/productos-rayosx`);
            const data = await response.json();
            setProductos(data.$values || data || []);
        } catch (error) {
            console.error('Error al cargar productos:', error);
        }
    };

    useEffect(() => {
        fetchProyecciones();
    }, []);

    useEffect(() => {
        if (editingItem) {
            setFormData({
                nombre: editingItem.nombre || editingItem.descripcion || '',
                codigo: editingItem.codigo || ''
            });
        } else {
            setFormData({ nombre: '', codigo: '' });
        }
    }, [editingItem]);

    const handleSave = async () => {
        if (!formData.nombre.trim()) {
            Swal.fire('Validación', 'El nombre es requerido', 'warning');
            return;
        }
        setLoading(true);
        try {
            const payload = {
                ...formData,
                ingresadoPor: user.codigoUsuario.padEnd(20),
                modificadoPor: user.codigoUsuario.padEnd(20)
            };
            const method = editingItem ? 'PUT' : 'POST';
            const url = editingItem
                ? `${server}/ImgProyecciones/${editingItem.idProyeccion || editingItem.id}`
                : `${server}/ImgProyecciones`;

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                Swal.fire('Éxito', editingItem ? 'Actualizado' : 'Creado', 'success');
                setEditingItem(null);
                setFormData({ nombre: '', codigo: '' });
                fetchProyecciones();
            } else {
                const errorData = await response.json();
                Swal.fire('Error', errorData.message || 'Error al guardar', 'error');
            }
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!itemToDelete) return;
        try {
            const username = user.codigoUsuario.padEnd(20) + '/';
            const response = await fetch(
                `${server}/ImgProyecciones/${itemToDelete.idProyeccion || itemToDelete.id}/${username}`,
                { method: 'DELETE' }
            );
            if (response.ok) {
                setOpenDialog(false);
                setItemToDelete(null);
                fetchProyecciones();
                Swal.fire('Éxito', 'Eliminado correctamente', 'success');
            } else {
                const errorData = await response.json();
                Swal.fire('Error', errorData.message || 'Error al eliminar', 'error');
            }
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        }
    };

    const handleOpenAsignar = async (item) => {
        setAsignarItem(item);
        await fetchProductos();
        try {
            const response = await fetch(`${server}/ImgProyecciones/${item.idProyeccion || item.id}/productos`);
            const data = await response.json();
            setProductosSeleccionados(data.$values || data || []);
        } catch (error) {
            console.error('Error al cargar asignaciones:', error);
            setProductosSeleccionados([]);
        }
        setOpenAsignar(true);
    };

    const handleGuardarAsignacion = async () => {
        if (!asignarItem) return;
        try {
            const response = await fetch(`${server}/ImgProyecciones/asignar`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    idProyeccion: asignarItem.idProyeccion || asignarItem.id,
                    idProductos: productosSeleccionados.map(p => p.idProducto || p),
                    ingresadoPor: user.codigoUsuario.padEnd(20)
                })
            });
            if (response.ok) {
                Swal.fire('Éxito', 'Asignación guardada', 'success');
                setOpenAsignar(false);
            } else {
                const errorData = await response.json();
                Swal.fire('Error', errorData.message || 'Error al asignar', 'error');
            }
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        }
    };

    const filteredItems = proyecciones.filter(item => {
        const nombre = (item.nombre || item.descripcion || '').toLowerCase();
        const codigo = (item.codigo || '').toLowerCase();
        const search = searchQuery.toLowerCase();
        return nombre.includes(search) || codigo.includes(search);
    });

    return (
        <div className="right_col" role="main">
        <div className="x_content">
            <div className="x_title">
                <h2>Configuración de Proyecciones</h2>
            </div>

            {/* Formulario */}
            <Box sx={{ padding: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
                <Typography variant="h6">
                    {editingItem ? 'Editar Proyección' : 'Nueva Proyección'}
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={5}>
                        <TextField
                            fullWidth
                            label="Nombre"
                            value={formData.nombre}
                            onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                            required
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="Código"
                            value={formData.codigo}
                            onChange={(e) => setFormData(prev => ({ ...prev, codigo: e.target.value }))}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <ButtonGroup>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSave}
                                disabled={loading || !tienePermiso(editingItem ? 'Editar Proyeccion' : 'Crear Proyeccion')}
                            >
                                {loading ? 'Guardando...' : 'Guardar'}
                            </Button>
                            {editingItem && (
                                <Button
                                    variant="outlined"
                                    onClick={() => { setEditingItem(null); setFormData({ nombre: '', codigo: '' }); }}
                                >
                                    Cancelar
                                </Button>
                            )}
                        </ButtonGroup>
                    </Grid>
                </Grid>
            </Box>

            <div className="ln_solid" />
            <h3>Listado de Proyecciones</h3>

            <TextField
                label="Buscar"
                fullWidth
                variant="outlined"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ marginBottom: '20px' }}
            />

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Nombre</TableCell>
                            <TableCell>Código</TableCell>
                            <TableCell align="right">Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => (
                            <TableRow key={item.idProyeccion || item.id}>
                                <TableCell>{item.nombre || item.descripcion}</TableCell>
                                <TableCell>{item.codigo}</TableCell>
                                <TableCell align="right">
                                    <Button
                                        onClick={() => setEditingItem(item)}
                                        disabled={!tienePermiso('Editar Proyeccion')}
                                        color="primary"
                                        size="small"
                                    >
                                        <EditIcon fontSize="small" /> Editar
                                    </Button>
                                    <Button
                                        onClick={() => handleOpenAsignar(item)}
                                        disabled={!tienePermiso('Asignar Proyeccion Producto')}
                                        color="info"
                                        size="small"
                                    >
                                        <LinkIcon fontSize="small" /> Asignar
                                    </Button>
                                    <Button
                                        onClick={() => { setItemToDelete(item); setOpenDialog(true); }}
                                        disabled={!tienePermiso('Eliminar Proyeccion')}
                                        color="error"
                                        size="small"
                                    >
                                        <DeleteIcon fontSize="small" /> Eliminar
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredItems.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                />
            </TableContainer>

            {/* Dialog Eliminar */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogTitle>Confirmar eliminación</DialogTitle>
                <DialogContent>
                    ¿Está seguro de eliminar la proyección "{itemToDelete?.nombre || itemToDelete?.descripcion}"?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
                    <Button onClick={handleDelete} color="error">Eliminar</Button>
                </DialogActions>
            </Dialog>

            {/* Dialog Asignar Productos */}
            <Dialog open={openAsignar} onClose={() => setOpenAsignar(false)} maxWidth="md" fullWidth>
                <DialogTitle>Asignar Productos a Proyección: {asignarItem?.nombre || asignarItem?.descripcion}</DialogTitle>
                <DialogContent>
                    <Autocomplete
                        multiple
                        options={productos}
                        getOptionLabel={(option) => option.descripcion || option.nombre || ''}
                        value={productosSeleccionados}
                        onChange={(e, newValue) => setProductosSeleccionados(newValue)}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    label={option.descripcion || option.nombre}
                                    {...getTagProps({ index })}
                                    key={option.idProducto || index}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField {...params} label="Productos de Rayos X" placeholder="Buscar producto..." sx={{ mt: 1 }} />
                        )}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenAsignar(false)}>Cancelar</Button>
                    <Button onClick={handleGuardarAsignacion} variant="contained" color="primary">
                        Guardar Asignación
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
        </div>
    );
}

export default ConfigProyecciones;
